// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from './AuthContext.js';

interface PermissionContextValue {
  permissions: string[];
  hasPermission: (perm: string) => boolean;
  hasAnyPermission: (perms: string[]) => boolean;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const permissions = user?.permissions || [];

  const value = useMemo<PermissionContextValue>(
    () => ({
      permissions,
      hasPermission: (perm: string) => permissions.includes(perm),
      hasAnyPermission: (perms: string[]) => perms.some((p) => permissions.includes(p)),
    }),
    [permissions],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissionContext(): PermissionContextValue {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error('usePermissionContext must be used within PermissionProvider');
  return ctx;
}
